import { MakeApiCall } from "../apis";
import { Types } from "../config";

export const SUB_USER_TYPE = 7;

export const SUB_USER_ROLES = {
  ANALYST: "analyst",
  MANAGER: "manager",
  ACCOUNT_ADMIN: "account_admin",
};

export const getSubUserRole = (session) => {
  const role = getUserDataFromSession(session)?.sub_user_role;
  return role ? String(role).toLowerCase() : null;
};

export const isSubUserAnalyst = (session) =>
  isSubUser(session) && getSubUserRole(session) === SUB_USER_ROLES.ANALYST;

/** Analyst sub-users are view-only; all other users can edit. */
export const canUserEdit = (session) => !isSubUserAnalyst(session);

export const PAGE_PERMISSION_TO_ROUTE = {
  dashboard: "dashboard",
  products: "settings/products",
  alerts: "alerts",
  expenses: "settings/expenses",
  expense_categories: "settings/expenses",
  ai_chat: "ai-copilot",
  imports_n_exports: "settings/file-manager",
  data_logs: "settings/file-manager",
  my_profile: "settings/myprofile",
  advanced_settings: "settings/advance-settings",
  marketplace_credentials: "settings/marketplace-credentials",
  user_settings: "settings/marketplace-credentials",
  billing: "settings/billing",
  child_sellers: "settings/manage-seller",
  sub_users: "sub-users",
};

const DEFAULT_ROUTE_PRIORITY = [
  "dashboard",
  "ai-copilot",
  "alerts",
  "settings/products",
  "settings/expenses",
  "settings/billing",
  "settings/file-manager",
  "sub-users",
  "settings/myprofile",
  "settings/marketplace-credentials",
  "settings/advance-settings",
  "settings/manage-seller",
];

export const getUserDataFromSession = (session) =>
  session?.user?.user_data ?? session?.user_data ?? null;

const hasSubUserFlag = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

export const isSubUser = (session) => {
  const userData = getUserDataFromSession(session);
  if (!userData) return false;
  if (Number(userData.user_type) === SUB_USER_TYPE) return true;
  return hasSubUserFlag(userData.is_sub_user);
};

export const resolveLoginType = (session) => {
  if (session?.loginType) return session.loginType;
  const userData = getUserDataFromSession(session);
  if (!userData) return undefined;
  return Types?.[Number(userData.user_type)];
};

export const getSubUserPagePermissions = (session) => {
  const userData = getUserDataFromSession(session);
  if (!userData) return [];

  const contextId = userData.seller_context_id ?? userData.owner_seller_id ?? userData.id;
  const sellerAccess = userData.seller_access || [];
  const currentAccess = sellerAccess.find(
    (entry) =>
      Number(entry.seller_user_id) === Number(contextId) && entry.enabled !== false
  );

  const permissions = currentAccess?.page_permissions ?? userData.page_permissions ?? [];
  return Array.isArray(permissions) ? permissions : [];
};

export const getAccessibleSellers = (session) =>
  getUserDataFromSession(session)?.accessible_sellers || [];

export const getActiveSellerAccountId = (session) => {
  const userData = getUserDataFromSession(session);
  if (!userData) return null;

  const activeId =
    userData.active_seller_account?.id ??
    userData.seller_context_id ??
    userData.owner_seller_id ??
    userData.accessible_sellers?.[0]?.id;

  return activeId != null ? Number(activeId) : null;
};

export const buildSessionFromAuthPayload = (data) => {
  const userType = Number(data?.user_data?.user_type);
  return {
    user: data,
    loginType: Types?.[userType],
    token: data?.auth_token_data?.access_token,
  };
};

export const switchSubUserToActiveSeller = async (session) => {
  const sellerId = getActiveSellerAccountId(session);
  if (!sellerId) return session;

  const response = await MakeApiCall(
    "switch-user",
    "post",
    { id: sellerId },
    true,
    {},
    session?.token
  );

  if (response?.status !== true) {
    throw new Error(response?.message || "Failed to switch seller account.");
  }

  const userType = Number(response?.data?.user_data?.user_type);
  return {
    ...session,
    user: response?.data,
    loginType: Types?.[userType],
    token: response?.data?.auth_token_data?.access_token,
  };
};

export const prepareSubUserSession = async (authPayload, existingSession = {}) => {
  const session = {
    ...existingSession,
    ...buildSessionFromAuthPayload(authPayload),
  };
  return switchSubUserToActiveSeller(session);
};

export const subUserNeedsSellerSwitch = (session) => {
  if (!isSubUser(session)) return false;

  const userData = getUserDataFromSession(session);
  const activeId = getActiveSellerAccountId(session);
  if (!activeId) return false;

  return Number(userData?.seller_context_id) !== activeId;
};

export const isSubUserDashboardAnalyticsEnabled = (session) => {
  if (!isSubUser(session)) return false;

  const userData = getUserDataFromSession(session);
  if (!userData?.seller_context_id) return false;

  return getSubUserPagePermissions(session).includes("dashboard");
};

export const getAllowedRouteKeys = (pagePermissions = []) => {
  const routes = new Set();
  pagePermissions.forEach((permission) => {
    const route = PAGE_PERMISSION_TO_ROUTE[permission];
    if (route) routes.add(route);
  });
  return routes;
};

export const normalizeRoutePath = (pathname = "") =>
  pathname.replace(/^\//, "").split("?")[0];

export const isRouteAllowedForSubUser = (pathname, pagePermissions = []) => {
  const route = normalizeRoutePath(pathname);
  if (!route) return true;
  return getAllowedRouteKeys(pagePermissions).has(route);
};

export const getSubUserDefaultRoute = (pagePermissions = []) => {
  const allowed = getAllowedRouteKeys(pagePermissions);
  const prioritized = DEFAULT_ROUTE_PRIORITY.find((route) => allowed.has(route));
  if (prioritized) return `/${prioritized}`;

  const first = [...allowed][0];
  return first ? `/${first}` : "/dashboard";
};

export const filterMenusForSubUser = (menus = [], pagePermissions = []) => {
  const allowed = getAllowedRouteKeys(pagePermissions);

  return menus
    .map((menu) => {
      if (menu.children?.length) {
        const children = menu.children.filter((child) => allowed.has(child.key));
        if (!children.length) return null;
        return { ...menu, children };
      }

      if (allowed.has(menu.key)) return menu;
      return null;
    })
    .filter(Boolean);
};

export const mapAccessibleSellersToList = (sellers = []) =>
  sellers.map((seller) => ({
    id: seller.id,
    name: seller.name || seller.seller_name || `Account ${seller.id}`,
    seller_name: seller.seller_name || seller.name || "",
    email: seller.email || "",
    photo: seller.photo || null,
    is_parent: !!seller.is_parent,
  }));
